'use client';

import UiIcon from '@/components/ui/UiIcon';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { apiClient, Organization, OrganizationType } from '@/lib/api';

// Иконки типов организаций
const TYPE_ICONS: Record<OrganizationType, string> = {
  university: '🏛️',
  college: '🎓',
  institute: '📐',
  department: '📊',
  laboratory: '🔬',
  school: '🏫',
  gymnasium: '📚',
  organization: '🏢',
};

interface OrganizationSelectProps {
  value?: number | null;
  onChange: (orgId: number | null, org: Organization | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  filterType?: OrganizationType | OrganizationType[];
  showClear?: boolean;
}

export default function OrganizationSelect({
  value,
  onChange,
  placeholder = 'Выберите организацию...',
  disabled = false,
  className = '',
  filterType,
  showClear = true,
}: OrganizationSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // Загрузка организаций
  const loadOrganizations = useCallback(async (searchQuery: string = '') => {
    setLoading(true);
    try {
      const params: any = { flat: true, active_only: true };
      if (searchQuery) params.search = searchQuery;
      
      const res = await apiClient.getOrganizations(params);
      if (res.success) {
        let orgs = res.data;
        // Фильтрация по типу если указано
        if (filterType) {
          const types = Array.isArray(filterType) ? filterType : [filterType];
          orgs = orgs.filter((o) => types.includes(o.type));
        }
        setOrganizations(orgs);
      }
    } catch (error) {
      console.error('Ошибка загрузки организаций:', error);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  // Загрузка при открытии
  useEffect(() => {
    if (isOpen) {
      loadOrganizations(search);
    }
  }, [isOpen, search, loadOrganizations]);

  // Загрузка выбранной организации при изменении value
  useEffect(() => {
    if (value && !selectedOrg) {
      apiClient.getOrganization(value).then((res) => {
        if (res.success) setSelectedOrg(res.data);
      }).catch(() => {});
    } else if (!value) {
      setSelectedOrg(null);
    }
  }, [value, selectedOrg]);

  // Вычисление позиции dropdown при открытии и при скролле/resize
  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen, updatePosition]);

  // Закрытие при клике вне и Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Debounce поиска
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isOpen) loadOrganizations(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, isOpen, loadOrganizations]);

  // Выбор организации
  const handleSelect = (org: Organization) => {
    setSelectedOrg(org);
    onChange(org.id, org);
    setIsOpen(false);
    setSearch('');
  };

  // Очистка
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOrg(null);
    onChange(null, null);
    setSearch('');
  };

  // Построение пути к организации (для отображения иерархии)
  const getOrgPath = (org: Organization): string => {
    const parts = [];
    if (org.parent_short_name || org.parent_name) {
      parts.push(org.parent_short_name || org.parent_name);
    }
    parts.push(org.short_name || org.name);
    return parts.join(' → ');
  };

  // Группировка по типам
  const groupedOrgs = useMemo(() => {
    const groups: Record<string, Organization[]> = {};
    for (const org of organizations) {
      const type = org.type;
      if (!groups[type]) groups[type] = [];
      groups[type].push(org);
    }
    return groups;
  }, [organizations]);

  const typeOrder: OrganizationType[] = ['university', 'college', 'institute', 'department', 'laboratory', 'school', 'gymnasium', 'organization'];

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Триггер */}
      <div
        ref={triggerRef}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all cursor-pointer ${
          disabled
            ? 'bg-site-surface/40 border-site-border cursor-not-allowed opacity-60'
            : isOpen
            ? 'bg-site-surface/80 border-site-accent ring-2 ring-site-accent/30'
            : 'bg-site-surface/60 border-site-border-control/50 hover:border-site-border-control/70'
        }`}
      >
        {selectedOrg ? (
          <>
            <span className="text-lg"><UiIcon name={TYPE_ICONS[selectedOrg.type] || 'Folder'} /></span>
            <span className="flex-1 text-site-text truncate">
              {selectedOrg.short_name || selectedOrg.name}
            </span>
            {showClear && !disabled && (
              <button
                onClick={handleClear}
                className="text-site-muted hover:text-site-text transition-colors p-1"
              >
                ✕
              </button>
            )}
          </>
        ) : (
          <>
            <span className="text-site-muted"><UiIcon name="Folder" /></span>
            <span className="flex-1 text-site-muted">{placeholder}</span>
          </>
        )}
        <svg
          className={`w-4 h-4 text-site-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Выпадающий список через Portal */}
      {isOpen && dropdownPosition && typeof document !== 'undefined' && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] rounded-xl border border-site-border bg-site-bg/95 backdrop-blur-md shadow-xl max-h-80 overflow-hidden"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          {/* Поиск */}
          <div className="p-2 border-b border-site-border">
            <input
              ref={inputRef}
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск организации..."
              className="w-full px-3 py-2 bg-site-surface/60 border border-site-border-control/50 rounded-lg text-site-text text-sm placeholder:text-site-muted focus:ring-2 focus:ring-site-accent focus:border-site-accent outline-none"
            />
          </div>

          {/* Список */}
          <div className="overflow-y-auto max-h-56">
            {loading ? (
              <div className="p-4 text-center text-site-muted">Загрузка...</div>
            ) : organizations.length === 0 ? (
              <div className="p-4 text-center text-site-muted">
                {search ? 'Ничего не найдено' : 'Нет организаций'}
              </div>
            ) : (
              <div className="py-1">
                {typeOrder.map((type) => {
                  const orgs = groupedOrgs[type];
                  if (!orgs || orgs.length === 0) return null;
                  
                  return (
                    <div key={type}>
                      <div className="px-3 py-1.5 text-xs font-medium text-site-muted uppercase tracking-wide bg-site-surface/30">
                        <UiIcon name={TYPE_ICONS[type]} /> {orgs[0]?.type_label || type}
                      </div>
                      {orgs.map((org) => (
                        <div
                          key={org.id}
                          onClick={() => handleSelect(org)}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                            value === org.id
                              ? 'bg-site-accent/20 text-site-text'
                              : 'hover:bg-site-surface/60 text-site-text'
                          }`}
                        >
                          <span className="text-lg"><UiIcon name={TYPE_ICONS[org.type] || 'Folder'} /></span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              {org.short_name || org.name}
                            </div>
                            {org.parent_name && (
                              <div className="text-xs text-site-muted truncate">
                                {org.parent_short_name || org.parent_name}
                              </div>
                            )}
                          </div>
                          {org.projects_count !== undefined && org.projects_count > 0 && (
                            <span className="text-xs text-site-muted bg-site-surface/60 px-2 py-0.5 rounded">
                              {org.projects_count} проект{org.projects_count === 1 ? '' : org.projects_count < 5 ? 'а' : 'ов'}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Подсказка */}
          {!loading && organizations.length > 0 && (
            <div className="px-3 py-2 text-xs text-site-muted border-t border-site-border bg-site-surface/30">
              {showClear && 'Нажмите ✕ для очистки • '}Найдено: {organizations.length}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
